/**
 * Имена коллекций.
 *
 * Каталог отдаёт их одним списком, а нужны они на нескольких экранах: в
 * запросе видно только адрес коллекции, а адрес человеку ничего не говорит.
 * Поэтому список тянется один раз и раздаётся всем — а не запрашивается
 * по коллекции на каждую карточку.
 */

import { Address } from '@ton/core';
import { useQuery } from '@tanstack/react-query';
import { friendlyAddress } from '@78/core';
import { fetchCollections } from './api.ts';

export interface CollectionName {
  name: string | null;
  image: string | null;
}

export function useCollectionNames(): (address: string) => CollectionName {
  const { data } = useQuery({
    queryKey: ['collections'],
    queryFn: fetchCollections,
    staleTime: 5 * 60 * 1000,
  });

  return (address: string) => {
    // Сравниваем адресами, а не строками: одна и та же коллекция приходит
    // то сырой, то дружественной, и совпадение по строке — случайность.
    const found = data?.collections.find((c) => sameAddress(c.collection, address));
    return { name: found?.name ?? null, image: found?.image ?? null };
  };
}

/** Короткая запись адреса: для мест, где имени нет, а адрес всё равно надо
 *  показать — целиком он занимает две строки и ничего не объясняет. */
export function shortAddress(address: string, opts: { wallet?: boolean } = {}): string {
  const friendly = friendlyAddress(address, opts);
  return friendly.length <= 16 ? friendly : `${friendly.slice(0, 6)}…${friendly.slice(-6)}`;
}

/**
 * Один ли это адрес.
 *
 * У адреса в TON три вида записи — сырой и два дружественных (EQ и UQ),
 * различающиеся контрольной суммой, — и строкой они не совпадают. Сравнивать
 * как есть уже приводило к тому, что админ не узнавал сам себя и раздел
 * управления не показывался вовсе.
 */
export function sameAddress(a: string, b: string): boolean {
  try {
    return Address.parse(a).equals(Address.parse(b));
  } catch {
    return a.toLowerCase() === b.toLowerCase();
  }
}

export { friendlyAddress };
